import map from 'lodash/map'
import { useCallback, useState } from 'react'

import { Name } from '@Globals/Resource'
import Sentry from '@Globals/Sentry'
import { Item, SearchItemsRequest } from '@Protos/info/info_pb'

import { getInfoClient } from '.'

export interface SuggestionLink {
  href: string
  id: string
  text: string
}

const SUGGESTION_LINK_CACHE = new Map<string, SuggestionLink[]>()

type SuggestionLinksHook = (
  storeName?: string,
) => [
  SuggestionLink[],
  (query: string) => Promise<void>,
]

export const useSuggestionLinks: SuggestionLinksHook = (storeName?: string) => {
  const [suggestionLinks, setSuggestionLinks] = useState<SuggestionLink[]>([])

  const fetchSuggestionLinks = useCallback(
    async (query: string) => {
      if (!storeName) {
        return
      }

      if (!query || query === '') {
        setSuggestionLinks([])
        return
      }

      const cacheKey = `${storeName}:${query}`
      if (SUGGESTION_LINK_CACHE.has(cacheKey)) {
        setSuggestionLinks(SUGGESTION_LINK_CACHE.get(cacheKey))
        return
      }

      const client = getInfoClient()

      const req = new SearchItemsRequest()
      req.setStoreName(storeName)
      req.setQuery(query)
      req.setPageSize(5)

      try {
        const res = await client.searchItems(req)

        const links = map<Item, SuggestionLink>(
          res.getItemsList(),
          item => ({
            href: item.getUrl(),
            id: new Name(item.getName()).id,
            text: item.getTitle(),
          }),
        )

        SUGGESTION_LINK_CACHE.set(cacheKey, links)

        setSuggestionLinks(links)
      } catch (err) {
        // TODO: remove temporary debugging
        Sentry.captureException(err, {
          extra: {
            grpc_request: JSON.stringify(req.toObject()),
          },
        })
      }
    },
    [storeName],
  )

  return [suggestionLinks, fetchSuggestionLinks]
}
